<template>
    <div
        id="app"
        class="h-100"
        :class="[skinClasses]"
    >
        <component :is="layout">
            <router-view />
        </component>

        <ScrollToTop v-if="enableScrollToTop" />
    </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue';
import { TOASTER_TARGET } from '@/utils/toasterTarget';

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig';
import { watch } from 'vue';
import useAppConfig from '@core/app-config/useAppConfig';
import { ErrorHandlerServiceMixin } from '@/@core/mixins/errorHandlerServiceMixin';
import { DialogServiceMixin } from '@/@core/mixins/dialogServiceMixin';
import { useWindowSize, useCssVar } from '@vueuse/core';
import { localize } from 'vee-validate';

import store from '@/store';

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue');
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue');
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue');

export default {
    components: {

        // Layouts
        LayoutHorizontal,
        LayoutVertical,
        LayoutFull,

        ScrollToTop,
    },
    mixins: [ErrorHandlerServiceMixin, DialogServiceMixin],
    // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
    // Currently, router.currentRoute is not reactive and doesn't trigger any change
    computed: {
        layout() {
            if (this.$route.meta.layout === 'full') return 'layout-full';
            return `layout-${this.contentLayoutType}`;
        },
        contentLayoutType() {
            return this.$store.getters['appConfig/layoutType'];
        },
    },
    watch: {
        '$i18n.locale': {
            handler: function(val) {
                // 表单校验的国际化处理
                val && localize(val);
            },
            immediate: true,
        },
    },
    beforeCreate() {
    // Set colors in theme
        const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark'];

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
        }

        // Set Theme Breakpoints
        const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];

        // eslint-disable-next-line no-plusplus
        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2));
        }

        // Set RTL
        const { isRTL } = $themeConfig.layout;
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    },
    setup() {
        const { skin, skinClasses } = useAppConfig();
        const { enableScrollToTop } = $themeConfig.layout;

        // If skin is dark when initialized => Add class to body
        if (skin.value === 'dark') document.body.classList.add('dark-layout');

        // Provide toast for Composition API usage
        // This for those apps/components which uses composition API
        // Demos will still use Options API for ease
        // provideToast({
        //     hideProgressBar: true,
        //     closeOnClick: false,
        //     closeButton: false,
        //     icon: false,
        //     timeout: 3000,
        //     transition: 'Vue-Toastification__fade',
        // });

        // Set Window Width in store
        store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth);
        const { width: windowWidth } = useWindowSize();
        watch(windowWidth, val => {
            store.commit('app/UPDATE_WINDOW_WIDTH', val);
        });

        return {
            skinClasses,
            enableScrollToTop,
        };
    },
    created() {
        this.getErrorHandlerService().setErrorDisplay(error_message => {
            return this.$bvToast.toast(error_message, {
                title: this.$t('backoffice.common.tips'),
                variant: 'danger',
                solid: true,
                toaster: TOASTER_TARGET,
            });
        });
        /**
         * 这里传入的是一个函数(options) =>{...} 整体
         * 把不变的统一处理
         */
        const dialogService = this.getDialogService();
        dialogService.registerDialogFunction((options) => {
            const {
                title,
                message,
                variant,
            } = options;
            this.$bvToast.toast(message, {
                title,
                variant,
                solid: true,
                toaster: TOASTER_TARGET,
            });
        });
        dialogService.registerConfigmFunction((option) => {
            const {
                title,
                html,
                confirmButtonText,
                cancelButtonText,
            } = option;
            return this.$swal({
                title,
                html,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText,
                cancelButtonText,
                customClass: {
                    confirmButton: 'btn btn-primary',
                    cancelButton: 'btn btn-outline-danger ml-1',
                },
                buttonsStyling: false,
            }).then(res => res.value);
        });
        window.addEventListener('hashchange', () => {
            const currentHash = window.location.hash.slice(1);
            if (this.$route.path !== currentHash) {
                this.$router.push(currentHash);
            }
        })
    },
};
</script>
